import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import SplitText from 'gsap/SplitText';
import Flip from 'gsap/Flip';
import { onWidthResize, offWidthResize } from '../utils/resize.js';
gsap.registerPlugin(ScrollTrigger, SplitText, Flip);

export default class StockistsCollage {
    constructor(container) {
        this.container = container;
        this.heading = this.container.querySelector('.stockists-collage__heading');

        this.body = this.container.querySelector('.stockists-collage__body');
        this.photos = this.container.querySelector('.stockists-collage__photos');
        this.velocityTimer = null;
        this.flowPhotoLimit = 3;
        this.flowPositions = ['top-left', 'top-right', 'bottom-right'];
        this.allPositions = ['top-left', 'top-right', 'mid-left', 'bottom-left', 'bottom-right'];
        this.figure_speeds = [46, 72, 38];
        this.collage__photos = this.photos?.querySelectorAll('.stockists-collage__photo') ?? [];
        this.init();
    }

    init = () => {
        const isDesktop = window.matchMedia('(min-width: 1201px)').matches;

        if (isDesktop) {
            requestAnimationFrame(() => {
                this.initInfiniteScroll();
            });

            if (this.heading) {

                this.headingEm = this.container.querySelector('.stockists-collage__heading em').cloneNode(true);
                this.container.appendChild(this.headingEm);
                gsap.set(this.headingEm, { position: 'absolute', top: '50%', left: '50%', xPercent: -50, yPercent: -50, zIndex: 100, });

                new SplitText(this.heading, { type: 'lines', linesClass: 'lineParent' });
                this.headingSplit = new SplitText(this.heading.querySelectorAll('.lineParent'), { type: 'words' });

                const nonEmWords = this.headingSplit.words
                gsap.set(nonEmWords, { yPercent: 100 });

                // Capture em's natural in-heading position BEFORE moving it to center


                if (this.body) {
                    this.bodySplit = new SplitText(this.body, { type: 'lines,words', linesClass: 'lineParent' });
                    gsap.set(this.bodySplit.words, { yPercent: 100 });
                }


                this.pinTrigger = gsap.timeline({ scrollTrigger: { trigger: this.container, start: 'center center', end: '+=250%', pin: true, scrub: true, }, });

                // Scrubbed gradient wipe: em text reveals from hidden → coloured as user scrolls
                this.pinTrigger.fromTo(this.headingEm,
                    {
                        backgroundImage: 'linear-gradient(to right, red 50%, black 50%)',
                        backgroundSize: '200% 100%',
                        backgroundPositionX: '100%',
                        backgroundClip: 'text',
                        webkitBackgroundClip: 'text',
                        color: 'transparent',
                    },
                    {
                        backgroundPositionX: '0%',
                        ease: 'none',
                        duration: 1,
                    }
                );
                this.pinTrigger.to(this.headingEm, { autoAlpha: 0, duration: 1, ease: 'none' });
                this.pinTrigger.fromTo(this.heading, { autoAlpha: 0, }, { autoAlpha: 1, duration: 1, ease: 'none' }, '>');
                this.pinTrigger.fromTo(this.body, { autoAlpha: 0, }, { autoAlpha: 1, duration: 1, ease: 'none' }, '>');
                this.pinTrigger.call(() => {
                    gsap.to(nonEmWords, {
                        yPercent: 0,
                        duration: 0.6,
                        delay: 0.2,
                        stagger: { each: 0.06, ease: 'power2.out', from: 'start' },
                        ease: 'power3.out',
                    });
                    if (this.bodySplit) {
                        gsap.to(this.bodySplit.words, {
                            yPercent: 0,
                            duration: 0.6,
                            delay: 0.2,
                            stagger: { each: 0.06, ease: 'power2.out', from: 'start' },
                            ease: 'power3.out',
                            onComplete: () => { this.onBodyResize(); },
                        });
                    }
                }, null, '>')


                this.pinTrigger.to(null, { duration: 2 })
            }
        } else {
            gsap.set(this.collage__photos, { y: 40, opacity: 0 });
            ScrollTrigger.batch(this.collage__photos, {
                onEnter: batch => gsap.to(batch, {
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    stagger: 0.12,
                    ease: 'power3.out',
                }),
                start: 'top 85%',
            });
            this.initTextAnimations();
        }
    }

    initTextAnimations = () => {
        if (this.heading) {
            this.headingSplit = new SplitText(this.heading, { type: 'lines,words', linesClass: 'lineParent', });
            gsap.fromTo(
                this.headingSplit.words,
                { yPercent: 100 },
                {
                    yPercent: 0,
                    duration: 0.5,
                    stagger: { each: 0.08, ease: 'power2.out', from: 'start' },
                    ease: 'power3.out',
                    scrollTrigger: { trigger: this.heading, start: 'top center', },
                    onComplete: () => { this.onHeadingResize() },
                }
            );
        }

        if (this.body) {
            this.bodySplit = new SplitText(this.body, { type: 'lines,words', linesClass: 'lineParent', });
            gsap.fromTo(
                this.bodySplit.words,
                { yPercent: 100 },
                {
                    yPercent: 0,
                    duration: 0.6,
                    delay: 0.2,

                    stagger: { each: 0.06, ease: 'power2.out', from: 'start' },
                    ease: 'power3.out',
                    scrollTrigger: { trigger: this.heading, start: 'top center', },
                    onComplete: () => onWidthResize(this.onBodyResize),
                }
            );
        }
    }

    onHeadingResize = () => {
        this.headingSplit?.revert();
    }

    onBodyResize = () => {
        this.bodySplit?.revert();
        offWidthResize(this.onBodyResize);
    }


    photoKey = (fig) => {
        if (fig.dataset.photo) return fig.dataset.photo;
        const img = fig.querySelector('img');
        const src = img?.getAttribute('src') || img?.currentSrc || '';
        return src.split('/').pop()?.split('?')[0]?.replace(/_\d+x.*$/, '') || src;
    }

    selectFlowPhotos = (figures) => {
        const unique = [];
        const seen = new Set();

        figures.forEach((fig) => {
            const key = this.photoKey(fig);
            if (key && seen.has(key)) return;
            if (key) seen.add(key);
            unique.push(fig);
        });

        const byPosition = new Map();
        unique.forEach((fig) => {
            const pos = fig.dataset.position;
            if (this.flowPositions.includes(pos) && !byPosition.has(pos)) {
                byPosition.set(pos, fig);
            }
        });

        const picked = [];
        this.flowPositions.forEach((pos) => {
            const fig = byPosition.get(pos);
            if (fig && !picked.includes(fig)) picked.push(fig);
        });
        unique.forEach((fig) => {
            if (picked.length < this.flowPhotoLimit && !picked.includes(fig)) {
                picked.push(fig);
            }
        });

        const usedSlots = new Set(
            picked.map((fig) => fig.dataset.position).filter((pos) => this.flowPositions.includes(pos))
        );
        const missingSlots = this.flowPositions.filter((pos) => !usedSlots.has(pos));
        picked.forEach((fig) => {
            if (this.flowPositions.includes(fig.dataset.position) || !missingSlots.length) return;
            const pos = missingSlots.shift();
            this.allPositions.forEach((slot) => {
                fig.classList.remove(`stockists-collage__photo--${slot}`);
            });
            fig.classList.add(`stockists-collage__photo--${pos}`);
            fig.dataset.position = pos;
        });

        figures.forEach((fig) => {
            fig.classList.toggle('stockists-collage__photo--idle', !picked.includes(fig));
        });

        return picked.slice(0, this.flowPhotoLimit);
    }

    initInfiniteScroll = () => {
        if (!this.photos) return;

        const containerEl = this.photos.closest('.stockists-collage__container');
        const H = (containerEl ?? this.container).offsetHeight;
        if (!H) return;

        const figures = this.selectFlowPhotos(Array.from(this.collage__photos));
        if (!figures.length) return;

        const tallest = figures.reduce((max, fig) => Math.max(max, fig.offsetHeight || 0), 0);
        this.loopH = Math.round(Math.max(H, window.innerHeight) + tallest + 200);
        this.targetMultiplier = 1;
        this.currentMultiplier = 1;

        const stride = this.loopH / figures.length;

        this.figureStates = figures.map((fig, i) => {
            const clone = fig.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            clone.classList.add('stockists-collage__photo--clone');
            this.photos.appendChild(clone);

            const speed = this.figure_speeds[i % this.figure_speeds.length];
            const y = this.loopH + i * stride;

            return {
                speed,
                y,
                clone,
                figSetter: gsap.quickSetter(fig, 'y', 'px'),
                cloneSetter: gsap.quickSetter(clone, 'y', 'px'),
            };
        });

        this.figureStates.forEach(({ y, figSetter, cloneSetter }) => {
            figSetter(y);
            cloneSetter(y + this.loopH);
        });

        this.tickerFn = (time, deltaTime) => this.tick(deltaTime);
        gsap.ticker.add(this.tickerFn);

        window.lenis?.on('scroll', this.onLenisScroll);
    }

    tick = (deltaTime) => {
        const dt = deltaTime / 1000;
        const H = this.loopH;
        this.currentMultiplier += (this.targetMultiplier - this.currentMultiplier) * 0.08;

        this.figureStates.forEach(state => {
            state.y -= state.speed * dt * this.currentMultiplier;

            if (state.y <= -H) state.y += H;
            state.figSetter(state.y);
            state.cloneSetter(state.y + H);
        });
    }

    onLenisScroll = ({ velocity }) => {
        if (!this.figureStates) return;

        this.targetMultiplier = 1 + Math.abs(velocity) * 1;

        clearTimeout(this.velocityTimer);
        this.velocityTimer = setTimeout(() => {
            this.targetMultiplier = 1;
        }, 250);
    }

    destroy = () => {
        this.pinTrigger?.kill();
        if (this.tickerFn) gsap.ticker.remove(this.tickerFn);
        clearTimeout(this.velocityTimer);
        window.lenis?.off('scroll', this.onLenisScroll);
        offWidthResize(this.onHeadingResize);
        offWidthResize(this.onBodyResize);
        this.figureStates?.forEach((state) => state.clone?.remove());
        this.figureStates = null;
        this.collage__photos.forEach((fig) => fig.classList.remove('stockists-collage__photo--idle'));
    }
}
